'use client';

import React from 'react';

import {Popover} from '@damascus-components/Popover';
import {ListItem, List as MUILIST} from '@mui/material';

import {useParams, usePathname, useSearchParams} from 'next/navigation';
import {
  clickOutsideEventListener,
  useClickOutside,
} from '@hooks/useClickOutside';
import {getNewLanguageRoute} from '@utils/getAppRoute';
import {
  LANGUAGE_OPTIONS,
  LanguageTypes,
} from '@launch-components/IntlProvider/IntlProvider';
import {Mobile} from '@components/Mobile';
import {Desktop} from '@components/Desktop';
import SVGIcons from '@components/SVGIcon';
import {NoTranslateWrapper} from '@components/NoTranslateWrapper';
import {Link} from '@components/Link';

import cn from './style.module.scss';

interface LanguageDropdownProps {
  options: {
    label: string;
    value: string;
  }[];
  className?: string;
}

export function LanguageDropdown(props: LanguageDropdownProps): JSX.Element {
  const [popupVisible, setVisible] = React.useState(false);
  const popoverContentRef = React.useRef<HTMLDivElement | null>(null);
  const selectInputRef = React.useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const {lang} = useParams();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  const handleClickOutside = React.useCallback(
    (event: MouseEvent) => {
      if (selectInputRef?.current?.contains(event.target as Node)) return;
      if (popupVisible) setVisible(false);
    },
    [popupVisible, selectInputRef]
  );

  useClickOutside(
    popoverContentRef,
    handleClickOutside as clickOutsideEventListener
  );

  const handleClick = () => {
    setVisible(true);
  };

  return (
    <div
      ref={popoverContentRef}
      className={`${cn.listItemContent} ${props.className ?? ''}`}>
      <Popover
        visible={popupVisible}
        enablePortal
        content={
          <>
            <MUILIST className={cn.languageBlock}>
              {props.options.map((item) => (
                <ListItem className={cn.languageBlock__item} key={item.value}>
                  <Link
                    query={query}
                    url={getNewLanguageRoute(
                      pathname,
                      item.value as LanguageTypes
                    )}>
                    <Desktop>
                      <NoTranslateWrapper>
                        <span className={cn.desktop}>{item.label}</span>
                      </NoTranslateWrapper>
                    </Desktop>
                    <Mobile>
                      <NoTranslateWrapper>
                        <span className={cn.desktop}>{item.value}</span>
                      </NoTranslateWrapper>
                    </Mobile>
                  </Link>
                </ListItem>
              ))}
            </MUILIST>
          </>
        }>
        <div
          ref={selectInputRef}
          className={cn.languageElement}
          onClick={handleClick}>
          <Desktop>
            <NoTranslateWrapper>
              <span>
                {LANGUAGE_OPTIONS.find(({value}) => value === lang)?.label ??
                  LANGUAGE_OPTIONS[0].label}
              </span>
            </NoTranslateWrapper>
          </Desktop>
          <Mobile>
            <NoTranslateWrapper>
              <span>{lang}</span>
            </NoTranslateWrapper>
          </Mobile>
          <span>
            <SVGIcons type='rightarrow-svg' />
          </span>
        </div>
      </Popover>
    </div>
  );
}
